/* eslint-disable */
"use client";
import { analyticsUrl } from "@/lib/analyticsUrl";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { shuffleArray } from "@/lib/shuffle";
import { useSession } from "next-auth/react"; // если логируешь userId
import { logAnalyticsEvent, buildAnalyticsBase } from "@/utils/hive"; // твоя функция отправки аналитики
import { useTimer } from "@/hooks/useTimer"; // таймер
import Hls from "hls.js";
// ---------- Types ----------
type RepeatMode = "none" | "track" | "playlist";

export interface Track {
  id?: number | string;
  title?: string;
  composer?: string;
  audioUrl?: string;
  duration?: string;
  // runtime instance marker to distinguish duplicates
  _instanceId?: string;
  // arbitrary extra fields from your DB shape
  [key: string]: any;
}

export interface Playlist {
  // your playlist shape may vary; keep it flexible
  id?: number | string;
  playlisttrack?: { song: Track }[];
  [key: string]: any;
}

interface AudioPlayerContextType {
  // state
  currentTrack: Track | null;
  currentPlaylistId: number | string | null;
  trackQueue: Track[];
  isPlaying: boolean;
  repeatMode: RepeatMode;
  shuffleMode: boolean;
  sleepTimer: number | null;
  sleepTimerStart: number | null;
  currentType: "playlist" | "work" | "userplaylist" | "song" | null;
  setCurrentType: React.Dispatch<
    React.SetStateAction<"playlist" | "work" | "userplaylist" | "song" | null>
  >;

  // setters
  setCurrentTrack: React.Dispatch<React.SetStateAction<Track | null>>;
  setCurrentPlaylistId: React.Dispatch<
    React.SetStateAction<number | string | null>
  >;
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  setRepeatMode: React.Dispatch<React.SetStateAction<RepeatMode>>;
  setShuffleMode: React.Dispatch<React.SetStateAction<boolean>>;
  setSleepTimer: React.Dispatch<React.SetStateAction<number | null>>;
  setSleepTimerStart: React.Dispatch<React.SetStateAction<number | null>>;

  // actions
  playTrack: (track: Track) => void;
  playPlaylist: (
    playlist: Playlist,
    playlistId?: number | string,
    type?: "playlist" | "work" | "userplaylist" | "song",
  ) => void;
  playTracks: (tracks: Track[]) => void;
  pause: () => void;
  resume: () => void; // 👈 добавь сюда
  finishPlayback: () => void;
}

// ---------- Context ----------
const AudioPlayerContext = createContext<AudioPlayerContextType | null>(null);

// ---------- Provider ----------
export function AudioPlayerProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const [currentPlaylistId, setCurrentPlaylistId] = useState<number | null>(
    null,
  );
  const [trackQueue, setTrackQueue] = useState<Track[]>([]); // 🎵 очередь треков
  const [isPlaying, setIsPlaying] = useState(false);
  const [repeatMode, setRepeatMode] = useState<RepeatMode>("none");
  const [shuffleMode, setShuffleMode] = useState(false);
  const [sleepTimer, setSleepTimer] = useState<number | null>(null);
  const [sleepTimerStart, setSleepTimerStart] = useState<number | null>(null);
  const { start, stop, reset, getElapsed } = useTimer();
  const lastSentDurationRef = useRef(0);
  const playbackSessionIdRef = useRef<string | null>(null);
  const playbackIsArrayRef = useRef(false);
  const { data: session } = useSession();
  const [currentType, setCurrentType] = useState<
    "playlist" | "work" | "userplaylist" | "song" | null
  >(null);

  const resume = () => {
    const audio =
      typeof document !== "undefined" ? document.querySelector("audio") : null;

    if (audio) {
      audio.play().catch(() => {});
    }

    setIsPlaying(true);
    start();
  };
  const logPlayStart = (
    track: Track,
    playlistId: number | string | null,
    type: "playlist" | "work" | "userplaylist" | "song" | null,
    eventType: "play_start" | "play_start_array" = "play_start",
  ) => {
    const payload = buildAnalyticsBase({
      session,
      currentTrack: track,
      currentPlaylistId: playlistId != null ? Number(playlistId) : null,
      currentType: type,
      durationSec: 0,
      referrer: analyticsUrl(document.referrer, window.location.origin),
      landingPage: analyticsUrl(window.location.href, window.location.origin),
    });

    payload.type = eventType;
    payload.playbackSessionId = playbackSessionIdRef.current;

    logAnalyticsEvent(payload);
  };
  const saveListeningHistory = async (
    track: Track,
    playlistId: number | string | null,
    type: "playlist" | "work" | "userplaylist" | "song" | null,
  ) => {
    const visitorId = localStorage.getItem("visitorId");

    try {
      const payload: {
        visitorId: string | null;
        songId?: number;
        playlistId?: number;
        workId?: number;
      } = {
        visitorId,
      };

      if (type === "song" && track.id) {
        payload.songId = Number(track.id);
      } else if (type === "playlist" && playlistId != null) {
        payload.playlistId = Number(playlistId);
      } else if (type === "work" && playlistId != null) {
        payload.workId = Number(playlistId);
      } else {
        return;
      }

      await fetch("/api/history", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });
    } catch (err) {
      console.error("[history] error:", err);
    }
  };
  const startPlaybackSession = () => {
    const id = crypto.randomUUID();
    playbackSessionIdRef.current = id;
    lastSentDurationRef.current = 0;
    return id;
  };

  const savePlaybackCheckpoint = () => {
    if (!playbackSessionIdRef.current) return;
    if (!isPlaying) return;

    const audio =
      typeof document !== "undefined"
        ? (document.querySelector("audio") as HTMLAudioElement | null)
        : null;

    if (!audio || !Number.isFinite(audio.currentTime)) return;

    const durationSec = Math.floor(audio.currentTime);

    // Не отправляем одно и то же значение повторно
    if (durationSec <= lastSentDurationRef.current) return;

    lastSentDurationRef.current = durationSec;

    const payload = buildAnalyticsBase({
      session,
      currentTrack,
      currentPlaylistId,
      currentType,
      durationSec,
    });

    payload.type = "play_update";
    payload.playbackSessionId = playbackSessionIdRef.current;

    logAnalyticsEvent(payload);
  };
  useEffect(() => {
    const onPageHide = () => {
      if (!isPlaying) return;

      const audio =
        typeof document !== "undefined"
          ? (document.querySelector("audio") as HTMLAudioElement | null)
          : null;

      if (!audio || !playbackSessionIdRef.current) return;

      const durationSec = Math.floor(audio.currentTime);

      const payload = buildAnalyticsBase({
        session,
        currentTrack,
        currentPlaylistId,
        currentType,
        durationSec,
      });

      payload.type = playbackIsArrayRef.current ? "play_array" : "play";
      payload.playbackSessionId = playbackSessionIdRef.current;

      try {
        const blob = new Blob([JSON.stringify(payload)], {
          type: "application/json",
        });

        navigator.sendBeacon("/api/hive", blob);
      } catch {
        // ignore
      }
    };

    window.addEventListener("pagehide", onPageHide);

    return () => {
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [isPlaying, currentTrack, currentPlaylistId, currentType, session]);
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        savePlaybackCheckpoint();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isPlaying, currentTrack, currentPlaylistId, currentType, session]);

  useEffect(() => {
    if (!isPlaying) return;

    const interval = window.setInterval(() => {
      savePlaybackCheckpoint();
    }, 15000);

    return () => {
      window.clearInterval(interval);
    };
  }, [isPlaying, currentTrack, currentPlaylistId, currentType, session]);

  const finalizeCurrentPlayback = () => {
    if (!isPlaying) return;

    const audio =
      typeof document !== "undefined"
        ? (document.querySelector("audio") as HTMLAudioElement | null)
        : null;

    const durationSec = audio ? Math.floor(audio.currentTime) : getElapsed();

    const payload = buildAnalyticsBase({
      session,
      currentTrack,
      currentPlaylistId,
      currentType,
      durationSec,
    });

    payload.type = playbackIsArrayRef.current ? "play_array" : "play";
    payload.playbackSessionId = playbackSessionIdRef.current;

    logAnalyticsEvent(payload);

    playbackSessionIdRef.current = null;
    playbackIsArrayRef.current = false;
    lastSentDurationRef.current = 0;

    stop();
    reset();
    setIsPlaying(false);
  };

  const addInstanceIds = (tracks: Track[]): Track[] =>
    tracks.map((track, index) => ({
      ...track,
      _instanceId: track._instanceId || `${track.id ?? "noid"}-${index}`,
    }));

  // 🔊 проигрывание одного трека
  const playTrack = (track: Track) => {
    finalizeCurrentPlayback();
    playbackIsArrayRef.current = false;
    startPlaybackSession();
    const tracksWithIds = addInstanceIds([track]);
    setTrackQueue(tracksWithIds);
    setCurrentTrack(tracksWithIds[0]);

    // 🔵 Логируем факт нажатия Play (гарантированно попадёт в серверные логи)
    fetch("/api/hive", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "play_button_click",
        songId: track.id,
        title: track.title,
        timestamp: new Date().toISOString(),
        userId: (session?.user as any)?.id ?? null,
        visitorId: localStorage.getItem("visitorId") ?? null,
        referer: analyticsUrl(document.referrer, window.location.origin),
        landingPage: analyticsUrl(window.location.href, window.location.origin),
      }),
      credentials: "include",
    }).catch((err) => console.error("[hive] play_button_click error:", err));

    setCurrentPlaylistId(null);

    setCurrentType("song");

    setIsPlaying(true);
    reset();
    start();

    const audio =
      typeof document !== "undefined"
        ? (document.querySelector("audio") as HTMLAudioElement)
        : null;
    if (audio && track.audioUrl) {
      const url = resolveAudioUrl(track);

      if (Hls.isSupported()) {
        const hls = new Hls();
        hls.loadSource(url);
        hls.attachMedia(audio);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          logPlayStart(track, null, "song");
          saveListeningHistory(track, null, "song");
          audio.play().catch(() => {});
        });
      } else {
        audio.src = url;

        logPlayStart(track, null, "song");
        saveListeningHistory(track, null, "song");
        audio.play().catch(() => {});
      }
    }
  };
  // 🔁 проигрывание всего плейлиста/ворка/пользовательского плейлиста
  const playPlaylist = (
    playlist: Playlist,
    playlistId?: number | string,
    type: "playlist" | "work" | "userplaylist" = "playlist",
  ) => {
    console.log(
      "[PLAYLIST CALLED]",
      new Date().toISOString(),
      new Error().stack,
    );
    finalizeCurrentPlayback();
    playbackIsArrayRef.current = false;
    startPlaybackSession();
    let tracks: Track[] = playlist.playlisttrack?.map((pt) => pt.song) || [];
    if (shuffleMode) {
      tracks = shuffleArray(tracks);
    }
    if (tracks.length > 0) {
      const tracksWithIds = addInstanceIds(tracks);
      setTrackQueue(tracksWithIds);
      setCurrentTrack(tracksWithIds[0]);

      // 🟣 Логируем факт нажатия Play на плейлисте
      fetch("/api/hive", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "play_button_click",
          playlistId: playlistId ?? playlist.id,
          title: playlist.title,
          timestamp: new Date().toISOString(),
          userId: (session?.user as any)?.id ?? null,
          visitorId: localStorage.getItem("visitorId") ?? null,
          referer: analyticsUrl(document.referrer, window.location.origin),
          landingPage: analyticsUrl(window.location.href, window.location.origin),
        }),
        credentials: "include",
      }).catch((err) => console.error("[hive] play_button_click error:", err));

      const idToSet = playlistId ?? playlist.id ?? null;

      setCurrentPlaylistId(idToSet != null ? Number(idToSet) : null);

      setCurrentType(type);

      setIsPlaying(true);
      reset();
      start();

      const audio =
        typeof document !== "undefined"
          ? (document.querySelector("audio") as HTMLAudioElement)
          : null;
      const track = tracksWithIds[0];
      if (audio && track?.audioUrl) {
        const url = resolveAudioUrl(track); // 👈 резолвер URL

        if (Hls.isSupported()) {
          const hls = new Hls();
          hls.loadSource(url);
          hls.attachMedia(audio);

          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            logPlayStart(track, idToSet, type);
            saveListeningHistory(track, idToSet, type);
            audio.play().catch(() => {});
          });
        } else {
          audio.src = url;

          logPlayStart(track, idToSet, type);
          saveListeningHistory(track, idToSet, type);
          audio.play().catch(() => {});
        }
      }
    }
  };
  // helper: строим HLS URL, если есть slug
  function resolveAudioUrl(track: Track): string {
    if (track.slug) {
      return `/api/audio/${track.slug}/${track.slug}.m3u8`;
    }
    return track.audioUrl;
  }

  // ⏸️ пауза + логирование аналитики
  const pause = () => {
  const audio =
    typeof document !== "undefined"
      ? (document.querySelector("audio") as HTMLAudioElement | null)
      : null;

  if (audio) {
    audio.pause();
  }

  // При паузе сохраняем текущую длительность,
  // но НЕ завершаем playback-сессию.
  if (playbackSessionIdRef.current && audio) {
    const durationSec = Math.floor(audio.currentTime);

    if (durationSec > lastSentDurationRef.current) {
      lastSentDurationRef.current = durationSec;

      const payload = buildAnalyticsBase({
        session,
        currentTrack,
        currentPlaylistId,
        currentType,
        durationSec,
      });

      payload.type = "play_update";
      payload.playbackSessionId = playbackSessionIdRef.current;

      logAnalyticsEvent(payload);
    }
  }

  stop();
  setIsPlaying(false);
};

  // ▶️ проигрывание массива треков
  const playTracks = (tracks: Track[]) => {
    finalizeCurrentPlayback();
    playbackIsArrayRef.current = true;
    startPlaybackSession();
    const tracksWithIds = addInstanceIds(tracks);
    const track = tracksWithIds[0];

    setTrackQueue(tracksWithIds);
    setCurrentTrack(track);
    setIsPlaying(true);
    start(); // 👈 запускаем таймер для массива треков
    if (track) {
      logPlayStart(track, null, null, "play_start_array");
    }
    const audio =
      typeof document !== "undefined" ? document.querySelector("audio") : null;
    if (audio && track) {
      try {
        const url = resolveAudioUrl(track); // 👈 резолвер URL
        audio.src = url;

        audio.currentTime = 0;
        audio.play().catch(() => {});
      } catch {
        // Ошибку формирования URL просто игнорируем
      }
    }
  };

  return (
    <AudioPlayerContext.Provider
      value={{
        currentTrack,
        playTrack,
        playPlaylist,
        pause,
        trackQueue,
        setCurrentTrack,
        isPlaying,
        setIsPlaying,
        currentPlaylistId,
        setCurrentPlaylistId, // 👈 если пригодится
        playTracks,
        repeatMode,
        setRepeatMode,
        shuffleMode,
        setShuffleMode,
        sleepTimer,
        setSleepTimer,
        sleepTimerStart,
        setSleepTimerStart,
        resume,
        currentType, // 👈 добавляем
        setCurrentType,
        finishPlayback: finalizeCurrentPlayback, // 👈 добавляемF
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
}

// ---------- Hook ----------
export function useAudioPlayer(): AudioPlayerContextType {
  const ctx = useContext(AudioPlayerContext);
  if (!ctx) {
    throw new Error(
      "useAudioPlayer must be used within an AudioPlayerProvider",
    );
  }
  return ctx;
}
